import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { StarIcon } from '@heroicons/react/24/solid';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

import TestimonialsImgAnna from "../Images/Testimonials/finland_flag.png";
import TestimonialsImgSjilda from "../Images/Testimonials/netherlandFlag.jpg";
import TestimonialsImgClarie from "../Images/Testimonials/france_flag.png";
import TestimonialsImgRoberto from "../Images/Testimonials/panama_flag.png";

const Testimonials = () => {
  const testimonials = [
    {
      name: 'Anna',
      position: 'Buyer',
      company: 'Helsinki',
      image: TestimonialsImgAnna,
      rating: 5,
      testimonial:
        'Our cooperation with Honeycomb Exports has been good. The team communicates clearly and keeps quality consistent.',
      orderSize: '50,000+ pieces yearly'
    },
    {
      name: 'Sjilda',
      position: 'Apparel Buyer',
      company: 'Amsterdam',
      image: TestimonialsImgSjilda,
      rating: 5,
      testimonial:
        'We have had a good working relationship with Honeycomb Exports for nearly fifteen years now.  Deliveries are as committed and communication with the team is clear. ',
      orderSize: '75,000+ pieces yearly'
    },
    {
      name: 'Claire',
      position: 'Buyer',
      company: 'Paris',
      image: TestimonialsImgClarie,
      rating: 5,
      testimonial:
        'For three years, Honeycomb Exports has been a dependable partner. They understand our specifications and adapt quickly when needed.',
      orderSize: '25,000+ pieces yearly'
    },
    {
      name: 'Roberto',
      position: 'Importer',
      company: 'Panama City',
      image: TestimonialsImgRoberto,
      rating: 5,
      testimonial:
        'We have worked with the Honeycomb Exports for ten years. They know our requirements well and have delivered as committed .',
      orderSize: '40,000+ pieces yearly'
    }
  ];

  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };
  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  // Auto-scroll with pause on hover or click
  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [isPaused, testimonials.length]);

  const handleDotClick = (idx) => {
    setCurrentTestimonial(idx);
    setIsPaused(true);
    setTimeout(() => setIsPaused(false), 5000); // resume auto-scroll after 5s
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.2 } }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
  };

  return (
    <section className="py-12 lg:py-12 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Testimonials */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.8 }}
          className="relative"
        >
          <div className="text-center mb-12">
            <h3 className="text-3xl font-display font-bold text-honeycomb-grey-900 mb-4">
              What Our Clients Say
            </h3>
            <p className="text-honeycomb-grey-600 max-w-2xl mx-auto">
              Real feedback from fashion brands who trust us with their manufacturing needs
            </p>
          </div>

          <div
            className="relative bg-honeycomb-grey-50 rounded-3xl p-8 lg:p-12 max-w-4xl mx-auto"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            <motion.div
              key={currentTestimonial}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.6 }}
              className="text-center"
            >
              {/* Stars */}
              <div className="flex justify-center mb-6">
                {[...Array(testimonials[currentTestimonial].rating)].map((_, i) => (
                  <StarIcon key={i} className="h-6 w-6 text-yellow-400" />
                ))}
              </div>

              {/* Testimonial Text */}
              <blockquote className="text-lg lg:text-xl text-honeycomb-grey-700 leading-relaxed mb-8 italic">
                "{testimonials[currentTestimonial].testimonial}"
              </blockquote>

              {/* Client Info */}
              <div className="flex items-center justify-center space-x-4">
                <img
                  src={testimonials[currentTestimonial].image}
                  alt={testimonials[currentTestimonial].name}
                  className="w-20 h-16"
                />
                <div className="text-left">
                  <h4 className="text-xl font-semibold text-honeycomb-grey-900">
                    {testimonials[currentTestimonial].name}
                  </h4>
                  <p className="text-honeycomb-grey-600">
                    {testimonials[currentTestimonial].position}
                  </p>
                  <p className="text-honeycomb-blue-600 font-semibold">
                    {testimonials[currentTestimonial].company}
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Navigation Buttons */}
            <button
              onClick={prevTestimonial}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 p-3 bg-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:bg-honeycomb-blue-50"
            >
              <ChevronLeftIcon className="h-6 w-6 text-honeycomb-grey-600" />
            </button>

            <button
              onClick={nextTestimonial}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 p-3 bg-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:bg-honeycomb-blue-50"
            >
              <ChevronRightIcon className="h-6 w-6 text-honeycomb-grey-600" />
            </button>

            {/* Dots Indicator */}
            <div className="flex justify-center space-x-2 mt-8">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => handleDotClick(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === currentTestimonial
                      ? 'bg-honeycomb-blue-600 scale-125'
                      : 'bg-honeycomb-grey-300 hover:bg-honeycomb-grey-400'
                  }`}
                />
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Testimonials;
